import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle, Plus, Send, Trash2 } from 'lucide-react'
import {
  Alert, Card, Field, Input, Loading, Select, Textarea, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, ConversionNote, MaterialPicker, NotPermitted,
  queueAttachments, type PendingAttachment,
} from '@/components/docs/shared'
import {
  TimberTally, blankTallyRow, isTimber, tallyCft, tallyFromJson, tallyToJson,
  type TallyRow,
} from '@/components/docs/TimberTally'
import { useAuth } from '@/lib/auth'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import { useMaterials, useSites, useVendors } from '@/lib/data'
import { useCompanySettings } from '@/lib/settings'
import { supabase } from '@/lib/supabase'
import {
  can, checkTolerance, countsWholePieces, isHoRole, iso, needsConversion,
  receiptToStock, round3, stockToPurchase,
} from '@/lib/domain'
import { qty, rupees } from '@/lib/format'
import { enqueue, reviseDocument } from '@/lib/sync'
import type { Material, PoLine, PurchaseOrder } from '@/lib/types'
import { PickedPo, PoPicker, isReceivable } from './PoPicker'
import { useSitePos } from './useSitePos'

interface Draft {
  key: string
  po_line_id: string | null
  material_id: string | null
  /** As printed on the vendor's challan — purchase UOM (KG for TMT). */
  qty_challan: string
  /** Physically counted — stock UOM (NOS for TMT). */
  qty_received: string
  qty_accepted: string
  qty_rejected: string
  unit_rate: string
  gst_rate: string
  po_remaining: number
  rejection_reason: string
  remarks: string
  /**
   * Has the store-man typed in this box himself?
   *
   * The three quantity boxes seed each other so the common case (everything
   * arrived, everything accepted) is one number and done. But the moment he
   * types a different number into a box, that box is his and nothing may
   * silently overwrite it again — a short delivery or a part-rejection must
   * survive him going back to correct a typo in an earlier box.
   */
  touched_received: boolean
  touched_accepted: boolean
  /** The timber tally sheet, for materials that are measured rather than counted. */
  tally: TallyRow[]
}

const draft = (): Draft => ({
  key: crypto.randomUUID(), po_line_id: null, material_id: null,
  qty_challan: '', qty_received: '', qty_accepted: '', qty_rejected: '',
  unit_rate: '', gst_rate: '', po_remaining: 0, rejection_reason: '', remarks: '',
  touched_received: false, touched_accepted: false, tally: [],
})

export function GrnNew() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const { id: editId } = useParams()
  const editing = !!editId
  const [loadingDoc, setLoadingDoc] = useState(editing)
  // A form that failed to load must not be offered for editing.
  const [editBlocked, setEditBlocked] = useState(false)

  const role = profile!.role
  const ho = isHoRole(role)
  const { data: sites } = useSites()
  const { data: vendors } = useVendors()
  const { data: materials } = useMaterials()
  // The over-receipt tolerance is a System Setting since 0184; 10% until the
  // administrator changes it, and 10% on a device that cannot read it.
  const { grnTolerancePct } = useCompanySettings()

  const [grnType, setGrnType] = useState<'PO_BASED' | 'ADHOC'>('PO_BASED')
  const [siteId, setSiteId] = useState(profile!.site_id ?? 'HO')
  const [poId, setPoId] = useState(params.get('po') ?? '')
  const [vendorId, setVendorId] = useState('')
  const [paymentMode, setPaymentMode] = useState<'PETTY_CASH' | 'CREDIT' | ''>('')
  const [receivedDate, setReceivedDate] = useState(iso(new Date()))
  const [challanNo, setChallanNo] = useState('')
  const [challanDate, setChallanDate] = useState(iso(new Date()))
  const [ewayBill, setEwayBill] = useState('')
  const [vehicleNo, setVehicleNo] = useState('')
  const [driverName, setDriverName] = useState('')
  const [inspection, setInspection] = useState('')
  const [remarks, setRemarks] = useState('')
  const [lines, setLines] = useState<Draft[]>([draft()])
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  /** The line the last failed submit complained about, so it can be pointed at. */
  const [badKey, setBadKey] = useState<string | null>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  // 02-11-2026: open orders for the site, for the card picker and the
  // ad-hoc nudge. Not read while correcting an existing receipt.
  const sitePos = useSitePos(editing ? '' : siteId)
  const [receivablePos, setReceivablePos] = useState<Array<{ po_id: string; vendor_name: string; site_id: string; quantity_pending: number }>>([])
  const [poLines, setPoLines] = useState<PoLine[]>([])
  const [loadingPo, setLoadingPo] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])
  const vendorLabel = useCallback(
    (vid: string) => vendors.find((v) => v.id === vid)?.company_name ?? vid, [vendors])

  // Correcting a goods receipt the Head of Stores sent back.
  useEffect(() => {
    if (!editId) return
    void (async () => {
      setLoadingDoc(true)
      try {
        const { error: guard } = await supabase.rpc('rpc_assert_editable', {
          p_doc_type: 'GRN', p_doc_id: editId,
        })
        if (guard) throw new Error(guard.message)

        const { data: h } = await supabase.from('grns').select('*').eq('id', editId).single()
        if (!h) throw new Error(`Goods receipt ${editId} was not found.`)
        const { data: rows } = await supabase.from('grn_line_items')
          .select('*').eq('grn_id', editId).order('sl_no')

        setGrnType(h.grn_type)
        setSiteId(h.site_id)
        setPoId(h.po_id ?? '')
        setVendorId(h.vendor_id ?? '')
        setReceivedDate(h.received_date ?? iso(new Date()))
        setChallanNo(h.challan_no ?? '')
        setChallanDate(h.challan_date ?? '')
        setEwayBill(h.eway_bill_no ?? '')
        setVehicleNo(h.vehicle_no ?? '')
        setDriverName(h.driver_name ?? '')
        setInspection(h.inspection_notes ?? '')
        setRemarks(h.remarks ?? '')

        // Outstanding on the PO has to include what this receipt itself claims,
        // otherwise correcting it downwards would look like a tolerance breach.
        const remaining: Record<string, number> = {}
        if (h.po_id) {
          const { data: pl } = await supabase.from('po_line_items').select('*').eq('po_id', h.po_id)
          for (const l of (pl ?? []) as PoLine[]) remaining[l.id] = Number(l.quantity_remaining)
        }

        setLines((rows ?? []).map((r: any) => ({
          key: crypto.randomUUID(),
          po_line_id: r.po_line_id,
          material_id: r.material_id,
          qty_challan: String(r.quantity_challan ?? ''),
          qty_received: String(r.quantity_received ?? ''),
          qty_accepted: String(r.quantity_accepted ?? ''),
          qty_rejected: String(r.quantity_rejected ?? 0),
          unit_rate: String(r.unit_rate ?? ''),
          gst_rate: String(r.gst_rate_percent ?? 0),
          po_remaining: (remaining[r.po_line_id] ?? 0) + Number(r.quantity_accepted ?? 0),
          rejection_reason: r.rejection_reason ?? '',
          remarks: r.remarks ?? '',
          touched_received: true,
          touched_accepted: true,
          tally: tallyFromJson(r.timber_tally),
        })))
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not open this goods receipt for correction')
        setEditBlocked(true)
      } finally {
        setLoadingDoc(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId])

  // Only VENDACK / partially-received POs for this site may be received against.
  useEffect(() => {
    if (editing) return
    void (async () => {
      let q = supabase.from('v_receivable_pos').select('*')
      if (siteId) q = q.eq('site_id', siteId)
      const { data } = await q.order('date_created', { ascending: false })
      setReceivablePos((data ?? []) as any)
    })()
  }, [siteId])

  useEffect(() => {
    // In edit mode the lines come from the receipt itself, not from the PO —
    // reloading the PO here would wipe out the quantities being corrected.
    if (editing) return
    if (!poId) { setPoLines([]); return }
    setLoadingPo(true)
    void (async () => {
      const { data: header } = await supabase.from('purchase_orders').select('*').eq('id', poId).maybeSingle()
      const { data: rows } = await supabase.from('po_line_items').select('*').eq('po_id', poId).order('sl_no')
      const po = header as PurchaseOrder | null
      if (po) { setVendorId(po.vendor_id); setSiteId(po.site_id) }
      const pending = ((rows ?? []) as PoLine[]).filter((l) => l.quantity_remaining > 0.0001)
      setPoLines(pending)
      setLines(pending.map((l) => ({
        key: crypto.randomUUID(),
        po_line_id: l.id,
        material_id: l.material_id,
        qty_challan: '',
        qty_received: '',
        qty_accepted: '',
        qty_rejected: '',
        unit_rate: String(l.unit_rate),
        gst_rate: String(l.gst_rate_percent),
        po_remaining: Number(l.quantity_remaining),
        rejection_reason: '',
        remarks: '',
        touched_received: false,
        touched_accepted: false,
        // Lines pulled from a purchase order start with an empty sheet; it
        // opens the moment the material turns out to be measured timber.
        tally: isTimber(byId[l.material_id]) ? [blankTallyRow(byId[l.material_id])] : [],
      })))
      setLoadingPo(false)
    })()
  }, [poId])

  if (!can(role, 'createGrn')) {
    return <NotPermitted what="record a goods receipt" who="site Store-man (or the HO Stores Executive on their behalf)" />
  }

  function update(key: string, patch: Partial<Draft>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
    // He is fixing it; stop shouting about it.
    if (badKey === key) { setBadKey(null); setError(null) }
  }

  /**
   * The tally sheet drives the counted quantity, never the other way round.
   *
   * On a measured material the store-man counts pieces and lengths; the volume
   * is arithmetic, so the Counted box is filled from the sheet and is read
   * only. setReceivedQty does the rest — seeding the challan and the accepted
   * boxes exactly as it does for any other material — so there is one place
   * where a quantity changes, not two.
   */
  function setTally(key: string, rows: TallyRow[]) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, tally: rows } : l)))
    setReceivedQty(key, rows.length ? String(tallyCft(rows)) : '')
  }

  /** Rejected is never typed — it is whatever was counted but not accepted. */
  function rejectedFrom(received: string, accepted: string): string {
    const r = Number(received) || 0
    const a = Number(accepted) || 0
    if (received === '') return ''
    return String(round3(Math.max(0, r - a)))
  }

  /**
   * Challan qty — what the vendor's paperwork says, in the purchase UOM (KG for
   * TMT bars). Seeds the counted box (converted into the stock UOM) and the
   * accepted box, but only while the store-man has not typed his own numbers
   * into them. A short delivery or a part-rejection he has already keyed must
   * never be silently overwritten by him going back to correct the challan.
   */
  function setChallanQty(key: string, value: string) {
    const l = lines.find((x) => x.key === key)
    if (!l) return
    const m = l.material_id ? byId[l.material_id] : null
    // Whole pieces for AAC blocks (NO counted, CUM billed); 3 decimals for everything else, as before.
    const stock = value === '' ? '' : String(receiptToStock(Number(value) || 0, m))

    const patch: Partial<Draft> = { qty_challan: value }
    if (!l.touched_received) {
      patch.qty_received = stock
      if (!l.touched_accepted) patch.qty_accepted = stock
    }
    const received = patch.qty_received ?? l.qty_received
    const accepted = patch.qty_accepted ?? l.qty_accepted
    patch.qty_rejected = rejectedFrom(received, accepted)
    update(key, patch)
  }

  /**
   * Counted — what he physically counted, in the stock UOM. This is his number
   * and it stays his. It back-fills the challan box only when that box is still
   * empty, so that counting 4,900 against a challan of 5,000 records the short
   * delivery instead of erasing it.
   */
  function setReceivedQty(key: string, value: string) {
    const l = lines.find((x) => x.key === key)
    if (!l) return
    const m = l.material_id ? byId[l.material_id] : null
    const factor = m?.conversion_factor ?? 1

    const patch: Partial<Draft> = { qty_received: value, touched_received: true }
    if (l.qty_challan === '' && value !== '') {
      patch.qty_challan = String(stockToPurchase(Number(value) || 0, factor))
    }
    // Accepted follows the count until he overrides it; once overridden it is
    // only pulled down if it would now exceed what was actually counted.
    let accepted = l.qty_accepted
    if (!l.touched_accepted) {
      accepted = value
    } else if (value !== '' && (Number(accepted) || 0) > (Number(value) || 0)) {
      accepted = value
    }
    patch.qty_accepted = accepted
    patch.qty_rejected = rejectedFrom(value, accepted)
    update(key, patch)
  }

  /** Accepted — what passed inspection. Cannot exceed what was counted. */
  function setAccepted(key: string, value: string) {
    const l = lines.find((x) => x.key === key)
    if (!l) return
    const received = Number(l.qty_received) || 0
    const clamped = value === '' ? '' : String(Math.min(Number(value) || 0, received))
    update(key, {
      qty_accepted: clamped,
      touched_accepted: true,
      qty_rejected: rejectedFrom(l.qty_received, clamped),
    })
  }

  const computed = lines.map((l) => {
    const m = l.material_id ? byId[l.material_id] : null
    const accepted = Number(l.qty_accepted) || 0
    const tol = grnType === 'PO_BASED' ? checkTolerance(accepted, l.po_remaining, grnTolerancePct) : null
    const value = accepted * (m?.conversion_factor ?? 1) * (Number(l.unit_rate) || 0)
    return { ...l, material: m, accepted, tolerance: tol, value }
  })

  const anyBreach = computed.some((c) => c.tolerance && !c.tolerance.withinTolerance)
  const payableValue = computed.reduce((a, c) => a + c.value, 0)
  const adhocGst = computed.reduce(
    (a, c) => a + c.value * ((Number(c.gst_rate) || 0) / 100), 0)

  /**
   * What is wrong, and WHERE.
   *
   * It used to return only a sentence. The sentence was drawn in a banner at
   * the top of the card while the Submit button sits at the foot of a form
   * that can run to three screens — so a store-man pressing Submit saw
   * absolutely nothing happen, which is what was reported on 09-09-2026. The
   * key travels with the message so the screen can take him to the line.
   */
  function validate(send: boolean): { msg: string; key?: string } | null {
    if (grnType === 'PO_BASED' && !poId) return { msg: 'Select the acknowledged purchase order this delivery is against.' }
    if (grnType === 'ADHOC' && !vendorId) return { msg: 'Select the vendor for this ad-hoc purchase.' }
    if (grnType === 'ADHOC' && !paymentMode) {
      return { msg: 'Say whether this was paid from site petty cash or bought on credit.' }
    }
    const filled = computed.filter((c) => c.material_id && Number(c.qty_received) > 0)
    if (filled.length === 0) return { msg: 'Enter the received quantity for at least one material.' }
    // A draft may be half-finished — the store-man is often keying it with the
    // lorry still at the gate. Only a receipt actually going to the Head of
    // Stores has to be complete.
    if (!send) return null
    if (grnType === 'ADHOC') {
      const noRate = filled.find((c) => !(Number(c.unit_rate) > 0))
      if (noRate) {
        return { key: noRate.key, msg:
          `Enter the rate on the ${noRate.material?.item_name ?? 'affected'} line. `
          + 'Without it the purchase is recorded as worth nothing and the company would '
          + 'owe money that appears nowhere.' }
      }
    }
    if (!challanNo.trim()) return { msg: "Enter the delivery challan number from the driver's paperwork." }
    const bad = filled.find((c) => Number(c.qty_accepted) + Number(c.qty_rejected || 0) > Number(c.qty_received) + 0.001)
    if (bad) return { key: bad.key, msg: 'Accepted plus rejected cannot exceed the received quantity.' }
    // AAC blocks are counted one by one - a part-block in stock could never be issued.
    const partPiece = filled.find((c) => countsWholePieces(c.material)
      && [c.qty_received, c.qty_accepted, c.qty_rejected].some((q) => !Number.isInteger(Number(q || 0))))
    if (partPiece) {
      return { key: partPiece.key, msg:
        `${partPiece.material?.item_name ?? 'This material'} is counted in whole ${partPiece.material?.unit_of_measure}. `
        + 'Enter the counted, accepted and rejected blocks as whole numbers.' }
    }
    const noReason = filled.find((c) => Number(c.qty_rejected) > 0 && !c.rejection_reason.trim())
    if (noReason) return { key: noReason.key, msg: `Say why material was rejected on the ${noReason.material?.item_name ?? 'affected'} line.` }
    // On a correction the original challan is already attached to the document.
    if (!editing && attachments.length === 0) {
      return { msg: 'Attach at least the delivery challan. Photographs and test certificates are strongly encouraged.' }
    }
    return null
  }

  async function submit(send = true) {
    const problem = validate(send)
    if (problem) {
      setError(problem.msg)
      setBadKey(problem.key ?? null)
      // Take him to it. A banner three screens away from the button he just
      // pressed is, from where he is standing, nothing happening at all.
      requestAnimationFrame(() => {
        const target = problem.key
          ? document.getElementById(`grn-line-${problem.key}`)
          : errorRef.current
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Put the cursor in the first box on that line that is still empty.
        const box = target?.querySelector<HTMLInputElement>('input:not([readonly]):placeholder-shown')
        box?.focus({ preventScroll: true })
      })
      return
    }
    setError(null)
    setBadKey(null)
    setBusy(true)
    try {
      const filled = computed.filter((c) => c.material_id && Number(c.qty_received) > 0)
      const localId = `GRN-LOCAL-${Date.now()}`
      const payload = {
        header: {
          grn_type: grnType,
          po_id: grnType === 'PO_BASED' ? poId : null,
          vendor_id: vendorId || null,
          payment_mode: grnType === 'ADHOC' ? paymentMode : null,
          site_id: siteId,
          status: send ? 'SUBMITTED' : 'DRAFT',
          received_date: receivedDate,
          challan_no: challanNo,
          challan_date: challanDate || null,
          eway_bill_no: ewayBill || null,
          vehicle_no: vehicleNo || null,
          driver_name: driverName || null,
          created_by: profile!.id,
          creator_email: profile!.email,
          inspection_notes: inspection || null,
          remarks: remarks || null,
          tolerance_breach: anyBreach,
        },
        lines: filled.map((c, i) => ({
          sl_no: i + 1,
          material_id: c.material_id,
          po_line_id: c.po_line_id,
          stock_uom: c.material!.unit_of_measure,
          purchase_uom: c.material!.purchase_uom,
          conversion_factor: c.material!.conversion_factor,
          quantity_challan: Number(c.qty_challan) || 0,
          quantity_received: Number(c.qty_received) || 0,
          quantity_accepted: Number(c.qty_accepted) || 0,
          quantity_rejected: Number(c.qty_rejected) || 0,
          unit_rate: Number(c.unit_rate) || 0,
          gst_rate_percent: Number(c.gst_rate) || 0,
          line_amount: Math.round(c.value * 100) / 100,
          tolerance_pct: c.tolerance?.variancePct ?? 0,
          within_tolerance: c.tolerance ? c.tolerance.withinTolerance : true,
          timber_tally: tallyToJson(c.tally),
          rejection_reason: c.rejection_reason || null,
          remarks: c.remarks || null,
        })),
      }

      // ---- correcting a returned receipt ----------------------------------
      if (editing) {
        const { header, lines: lineRows } = payload
        const { status: _drop, created_by: _drop2, creator_email: _drop3, ...editableHeader } = header
        await reviseDocument({
          docType: 'GRN',
          docId: editId!,
          header: editableHeader,
          lines: lineRows,
          submit: true,
          comment: 'Corrected and resubmitted after being returned',
        })
        if (attachments.length) await queueAttachments('GRN', editId!, attachments)
        toast.success('Corrected goods receipt sent back to the Head of Stores.')
        navigate(`/grns/${editId}`)
        return
      }

      const entry = await enqueue({
        kind: 'grn.create', docType: 'GRN', localDocId: localId,
        label: `${send ? 'Goods receipt' : 'Draft goods receipt'} at ${siteId} — challan ${challanNo || 'not yet entered'}`,
        payload,
      })

      await queueAttachments('GRN', entry.server_doc_id ?? localId, attachments)

      toast.success(!send
        ? 'Saved as a draft. Nothing has gone to the Head of Stores and no stock has moved.'
        : navigator.onLine
          ? 'Goods receipt submitted to the Head of Stores. Stock will increase once he approves it.'
          : 'Saved on this device with your photographs. It uploads by itself when you are back online.')
      navigate('/grns')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the goods receipt')
    } finally {
      setBusy(false)
    }
  }

  if (loadingDoc) return <Loading label="Opening the goods receipt…" />

  if (editBlocked) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title="This goods receipt cannot be corrected">{error}</Alert>
        <button className="btn-secondary" onClick={() => navigate(`/grns/${editId}`)}>
          Back to the goods receipt
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card
        title={editing ? `Correct goods receipt ${editId}` : 'Receive material'}
        subtitle={editing
          ? 'Make the changes the Head of Stores asked for, then send it back. The receipt keeps its number, its attachments and its history.'
          : 'Inspect the consignment, then record exactly what arrived and what you accepted'}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Receipt type" required
                 hint={grnType === 'ADHOC' ? 'For emergency purchases made at site with no purchase order' : undefined}>
            <Select
              value={grnType} disabled={editing}
              onChange={(e) => setGrnType(e.target.value as 'PO_BASED' | 'ADHOC')}
              options={[
                { value: 'PO_BASED', label: 'Against a purchase order' },
                { value: 'ADHOC', label: 'Ad-hoc / emergency site purchase' },
              ]}
            />
          </Field>

          <Field label="Site" required>
            <Select
              value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={!ho}
              options={sites.map((s) => ({ value: s.id, label: `${s.site_name} (${s.site_code})` }))}
            />
          </Field>

          {grnType === 'PO_BASED' && editing ? (
            <Field label="Purchase order" required className="lg:col-span-2"
                   hint="Only orders the vendor has acknowledged appear here">
              <Select
                value={poId} onChange={(e) => setPoId(e.target.value)}
                placeholder={receivablePos.length ? 'Select the purchase order…' : 'No acknowledged orders for this site'}
                options={receivablePos.map((p) => ({
                  value: p.po_id, label: `${p.po_id} · ${p.vendor_name} · ${qty(p.quantity_pending)} pending`,
                }))}
              />
            </Field>
          ) : grnType === 'PO_BASED' ? (
            // 02-11-2026: the order picker - a card per open order instead of
            // a drop-down, so the store-man can match the challan to it.
            <Field label="Purchase order" required className="sm:col-span-2 lg:col-span-4"
                   hint={poId ? undefined : 'Only acknowledged orders can be received. Greyed ones are waiting for Procurement.'}>
              {poId ? (
                <PickedPo po={sitePos?.find((p) => p.id === poId)} poId={poId} byId={byId}
                          vendorName={vendorLabel}
                          onChange={() => { setPoId(''); setLines([draft()]) }} />
              ) : (
                <PoPicker pos={sitePos} byId={byId} vendorName={vendorLabel} onPick={setPoId} />
              )}
            </Field>
          ) : (
            <Field label="Vendor" required className="lg:col-span-2"
                   hint="If the shop is not on the system, choose OTHERS — Procurement will name it later">
              <Select
                value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                placeholder="Select the vendor…"
                options={[
                  // The placeholder for THIS site, first, so it is easy to reach.
                  ...(siteId ? [{ value: `VEN-OTHERS-${siteId}`,
                                  label: 'OTHERS — a local shop, not yet on the system' }] : []),
                  ...vendors
                    .filter((v) => !(v as { is_placeholder?: boolean }).is_placeholder)
                    .map((v) => ({ value: v.id, label: v.company_name })),
                ]}
              />
            </Field>
          )}
        </div>

        {/* 02-11-2026: the case that makes work for Procurement - an
            ad-hoc receipt from a vendor who has an order open at this site. */}
        {!editing && grnType === 'ADHOC' && vendorId && (() => {
          const open = (sitePos ?? []).filter((p) => p.vendor_id === vendorId && isReceivable(p.status))
          if (open.length === 0) return null
          return (
            <div className="mt-4">
              <Alert tone="warning"
                     title={`${vendorLabel(vendorId)} has ${open.length} open purchase order${open.length === 1 ? '' : 's'} at this site`}>
                <p>
                  If this lorry is delivering against one of them ({open.map((p) => p.id).join(', ')}),
                  receive it against the order - otherwise Procurement has to link it by hand later.
                  Carry on with ad-hoc only if it really is a separate local purchase.
                </p>
                <button type="button" className="btn-primary btn-sm mt-3"
                        onClick={() => { setGrnType('PO_BASED'); setPoId(open.length === 1 ? open[0].id : '') }}>
                  Receive against {open.length === 1 ? open[0].id : 'one of the orders'} instead
                </button>
              </Alert>
            </div>
          )
        })()}

        {grnType === 'ADHOC' && (
          <div className="mt-4">
            <Field label="How was this purchase paid for?" required
                   hint="This decides whether the company still owes the money">
              <div className="grid gap-2 sm:grid-cols-2">
                <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${
                  paymentMode === 'PETTY_CASH'
                    ? 'border-oval-400 bg-oval-50 ring-1 ring-oval-200'
                    : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="paymode" className="mt-1"
                         checked={paymentMode === 'PETTY_CASH'}
                         onChange={() => setPaymentMode('PETTY_CASH')} />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">
                      Already paid — site petty cash
                    </span>
                    <span className="block text-xs text-slate-500">
                      The money has already left the site. A cash payment is recorded and
                      the bill is closed straight away — nothing is left owing.
                    </span>
                  </span>
                </label>

                <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${
                  paymentMode === 'CREDIT'
                    ? 'border-oval-400 bg-oval-50 ring-1 ring-oval-200'
                    : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="paymode" className="mt-1"
                         checked={paymentMode === 'CREDIT'}
                         onChange={() => setPaymentMode('CREDIT')} />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">
                      Credit purchase — the vendor will bill us
                    </span>
                    <span className="block text-xs text-slate-500">
                      A normal payable is raised. It is invoiced, matched and paid later,
                      exactly like a purchase against an order.
                    </span>
                  </span>
                </label>
              </div>
            </Field>
          </div>
        )}

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DocumentDate label="Received on" value={receivedDate} onChange={setReceivedDate}
                        hint="The day the lorry actually arrived, as written on the challan." />
          <Field label="Delivery challan no." required>
            <Input value={challanNo} onChange={(e) => setChallanNo(e.target.value)} placeholder="As printed on the challan" />
          </Field>
          <DocumentDate label="Challan date" value={challanDate} onChange={setChallanDate}
                        required={false} hint="As printed on the vendor's challan." />
          <Field label="E-way bill no.">
            <Input value={ewayBill} onChange={(e) => setEwayBill(e.target.value)} />
          </Field>
          <Field label="Vehicle number">
            <Input value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="TR01AB1234" />
          </Field>
          <Field label="Driver name">
            <Input value={driverName} onChange={(e) => setDriverName(e.target.value)} />
          </Field>
        </div>
      </Card>

      <div ref={errorRef}>{error && <Alert tone="error">{error}</Alert>}</div>
      {loadingPo && <Loading label="Loading purchase order lines…" />}

      {anyBreach && (
        <Alert tone="warning" title={`Tolerance exceeded (limit ${grnTolerancePct}%)`}>
          One or more lines accept more than {grnTolerancePct}% above the outstanding purchase-order quantity.
          You can still submit — the Head of Stores will see the breach flagged on his approval screen.
        </Alert>
      )}

      <Card
        title="What arrived"
        subtitle="Enter the challan quantity, then what you actually counted, then how much passed inspection. Rejected is worked out for you. Anything rejected is excluded from stock and from the vendor payable."
        actions={grnType === 'ADHOC' && (
          <button className="btn-secondary btn-sm" onClick={() => /* prepend: the blank row appears at the top so nobody scrolls to the bottom of a long document to type the next line. Agreed 09-09-2026. */ setLines((l) => [draft(), ...l])}>
            <Plus className="h-4 w-4" /> Add material
          </button>
        )}
      >
        {computed.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-500">
            {grnType === 'PO_BASED' ? 'Select a purchase order to load its pending lines.' : 'Add the materials you bought.'}
          </p>
        )}

        <div className="space-y-3">
          {computed.map((c, i) => {
            const m = c.material
            const convert = needsConversion(m)
            const timber = isTimber(m)
            return (
              <div key={c.key} id={`grn-line-${c.key}`}
                   className={`rounded-lg border p-3 ${
                     badKey === c.key ? 'border-red-400 bg-red-50/50 ring-2 ring-red-300'
                     : c.tolerance && !c.tolerance.withinTolerance ? 'border-amber-300 bg-amber-50/40'
                     : 'border-slate-200'}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400">LINE {i + 1}</span>
                  {grnType === 'ADHOC' && lines.length > 1 && (
                    <button className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                            onClick={() => setLines((ls) => ls.filter((x) => x.key !== c.key))}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="grid gap-3 lg:grid-cols-12">
                  <Field label="Material" required className="lg:col-span-4">
                    {grnType === 'PO_BASED' ? (
                      <div className="field bg-slate-50">
                        <span className="block truncate text-sm font-medium">{m?.item_name ?? c.material_id}</span>
                        <span className="block text-xs text-slate-500">
                          Outstanding on the PO: {qty(c.po_remaining)} {m?.unit_of_measure}
                        </span>
                      </div>
                    ) : (
                      <MaterialPicker
                        value={c.material_id}
                        onChange={(mm) => update(c.key, {
                          material_id: mm?.id ?? null, gst_rate: String(mm?.gst_rate ?? 0),
                          // Timber opens its sheet straight away, with the
                          // cross-section already filled in from the master.
                          tally: isTimber(mm) ? [blankTallyRow(mm)] : [],
                          qty_received: '', qty_accepted: '', qty_rejected: '',
                          touched_received: false, touched_accepted: false,
                        })}
                        autoFocus={false}
                      />
                    )}
                  </Field>

                  <Field label={`Challan qty (${m?.purchase_uom ?? '—'})`} className="lg:col-span-2"
                         hint={convert ? 'As written by the vendor' : undefined}>
                    <Input type="number" inputMode="decimal" min="0" step="any"
                           value={c.qty_challan} onChange={(e) => setChallanQty(c.key, e.target.value)} />
                  </Field>

                  <Field label={`Counted (${m?.unit_of_measure ?? '—'})`} required className="lg:col-span-2"
                         hint={timber ? 'Footed from the tally sheet below'
                               : convert ? 'What you physically counted' : undefined}>
                    <Input type="number" inputMode="decimal" min="0" step="any"
                           value={c.qty_received} onChange={(e) => setReceivedQty(c.key, e.target.value)}
                           readOnly={timber}
                           title={timber
                             ? 'On measured timber this is the total of the tally sheet below. Change a length there.'
                             : undefined}
                           className={timber ? 'cursor-not-allowed bg-amber-50 font-semibold'
                                     : convert ? 'bg-oval-50/60' : ''} />
                  </Field>

                  <Field label={`Accepted (${m?.unit_of_measure ?? '—'})`} required className="lg:col-span-2"
                         hint="Defaults to the counted quantity — reduce it if you are rejecting any">
                    <Input type="number" inputMode="decimal" min="0" step="any"
                           value={c.qty_accepted} onChange={(e) => setAccepted(c.key, e.target.value)} />
                  </Field>

                  <Field label={`Rejected (${m?.unit_of_measure ?? '—'})`} className="lg:col-span-2"
                         hint="Counted minus accepted">
                    <Input type="number" value={c.qty_rejected} readOnly tabIndex={-1}
                           className="bg-slate-100 text-slate-600" />
                  </Field>

                  {/* On a PO-based receipt the rate is copied from the order and must
                      not be editable here. On an ad-hoc purchase there IS no order, so
                      it has to be typed — and without it the payable is raised for
                      nothing at all. */}
                  {grnType === 'ADHOC' && (
                    <>
                      <Field label={`Rate per ${m?.purchase_uom ?? m?.unit_of_measure ?? 'unit'}`}
                             required className="lg:col-span-2"
                             hint="What was actually paid, before GST">
                        <Input type="number" inputMode="decimal" min="0" step="any"
                               value={c.unit_rate}
                               onChange={(e) => update(c.key, { unit_rate: e.target.value })}
                               invalid={Number(c.qty_accepted) > 0 && !(Number(c.unit_rate) > 0)} />
                      </Field>

                      <Field label="GST %" className="lg:col-span-2"
                             hint="Leave 0 for a plain cash memo">
                        <Input type="number" inputMode="decimal" min="0" max="28" step="any"
                               value={c.gst_rate}
                               onChange={(e) => update(c.key, { gst_rate: e.target.value })} />
                      </Field>

                      <div className="lg:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Line value
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-slate-800">
                          {rupees(c.value)}
                        </p>
                      </div>
                    </>
                  )}

                  {Number(c.qty_rejected) > 0 && (
                    <Field label="Why was material rejected?" required className="lg:col-span-6"
                           hint={!c.rejection_reason.trim()
                             ? `${qty(Number(c.qty_rejected))} ${m?.unit_of_measure ?? ''} is being rejected — this cannot be left blank`
                             : undefined}>
                      {/* Marked red as soon as a rejection exists without a reason,
                          rather than only when he presses Submit at the foot of
                          a three-screen form. */}
                      <Input value={c.rejection_reason}
                             invalid={!c.rejection_reason.trim()}
                             onChange={(e) => update(c.key, { rejection_reason: e.target.value })}
                             placeholder="e.g. 4 bars bent beyond tolerance, rusted surface" />
                    </Field>
                  )}

                  <Field label="Remarks" className={Number(c.qty_rejected) > 0 ? 'lg:col-span-6' : 'lg:col-span-12'}>
                    <Input value={c.remarks} onChange={(e) => update(c.key, { remarks: e.target.value })} />
                  </Field>
                </div>

                {/* The tally sits under the line it measures, so the sheet and
                    the quantity it produces are read together. */}
                {timber && m && (
                  <div className="mt-3">
                    <TimberTally material={m} rows={c.tally}
                                 onChange={(rows) => setTally(c.key, rows)} />
                  </div>
                )}

                <ConversionNote material={m} stockQty={Number(c.qty_received) || undefined} />

                {c.tolerance && !c.tolerance.withinTolerance && (
                  <p className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-100 px-2 py-1 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {c.tolerance.message}
                  </p>
                )}
                {c.value > 0 && (
                  <p className="mt-1 text-right text-xs text-slate-500">
                    Payable for this line (accepted only): <strong>{rupees(c.value)}</strong>
                  </p>
                )}
              </div>
            )
          })}
        </div>

        {payableValue > 0 && (
          <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-right">
            <p className="text-xs text-slate-500">
              Vendor payable that will be raised on approval (accepted quantity only)
            </p>
            {/* On an ad-hoc purchase the GST is typed on the line, so the total can
                be shown in full. On a PO-based receipt it comes from the order and
                is not known here. */}
            {grnType === 'ADHOC' ? (
              <>
                <p className="text-lg font-bold tabular-nums text-slate-900">
                  {rupees(payableValue + adhocGst)}
                </p>
                <p className="text-xs text-slate-400">
                  {rupees(payableValue)} plus GST {rupees(adhocGst)}
                  {paymentMode === 'PETTY_CASH'
                    && ' — and settled at once, because the site has already paid'}
                </p>
              </>
            ) : (
              <>
                <p className="text-lg font-bold tabular-nums text-slate-900">{rupees(payableValue)}</p>
                <p className="text-xs text-slate-400">plus GST as applicable on the connected purchase order</p>
              </>
            )}
          </div>
        )}
      </Card>

      <Card title="Inspection and documents">
        <div className="space-y-4">
          <Field label="Inspection notes"
                 hint="Condition of the material, packing, markings, anything the Head of Stores should know">
            <Textarea rows={2} value={inspection} onChange={(e) => setInspection(e.target.value)} />
          </Field>
          <AttachmentCollector docType="GRN" value={attachments} onChange={setAttachments} required />
          <Field label="Remarks">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Field>
        </div>
      </Card>

      <div className="sticky bottom-20 z-10 flex flex-wrap justify-end gap-2 lg:bottom-4">
        <button className="btn-ghost" onClick={() => navigate('/grns')} disabled={busy}>Cancel</button>
        {!editing && (
          <button className="btn-secondary" onClick={() => submit(false)} disabled={busy}>
            Save as draft
          </button>
        )}
        <button className="btn-primary" onClick={() => submit(true)} disabled={busy}>
          <Send className="h-4 w-4" /> {editing ? 'Resubmit to Head of Stores' : 'Submit to Head of Stores'}
        </button>
      </div>
    </div>
  )
}
